import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Check, Sparkles, Rocket, Crown, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../contexts/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_DETAILS = {
  starter: {
    icon: Sparkles,
    color: '#64748B',
    features: [
      'Up to 10 active listings',
      'AI listing writer',
      'WhatsApp & email lead alerts',
      'ID-verified buyer leads',
      'Standard support',
    ],
  },
  growth: {
    icon: Rocket,
    color: '#E07A3A',
    popular: true,
    features: [
      'Unlimited listings',
      'AI lead scoring (Hot/Warm/Cold)',
      'AI listing writer + floor plans',
      'Market snapshot reports',
      '3D tours + AR remodelling',
      'Priority lead alerts',
    ],
  },
  agency: {
    icon: Crown,
    color: '#0F172A',
    features: [
      'Everything in Growth',
      'White-label agent profile',
      'API access',
      'Dedicated account manager',
      'Vendor portal',
      'Early access to new features',
    ],
  },
};

const formatAmount = (amount, currency) => {
  const symbols = { gbp: '£', inr: '₹', cad: 'C$', usd: '$' };
  const sym = symbols[currency] || currency.toUpperCase() + ' ';
  return `${sym}${amount.toLocaleString()}`;
};

const SubscriptionPlans = () => {
  const { country: userCountry } = useCountry();
  const [packages, setPackages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPackage, setBusyPackage] = useState(null);
  const [country, setCountry] = useState(userCountry || 'UK');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgs, sub] = await Promise.all([
        axios.get(`${API_URL}/api/payments/packages?country=${country}`),
        axios.get(`${API_URL}/api/payments/me`),
      ]);
      setPackages(pkgs.data.packages || []);
      setSubscription(sub.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [country]);

  useEffect(() => { load(); }, [load]);

  const handleSubscribe = async (packageId) => {
    setBusyPackage(packageId);
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout/session`, {
        package_id: packageId,
        country,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Payment init failed — please try again.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment init failed');
    } finally {
      setBusyPackage(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="subscription-plans">
      <div>
        <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium flex items-center gap-2">
          <Sparkles className="h-3 w-3" /> Subscription
        </span>
        <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Your Plan</h2>
      </div>

      {/* Current subscription status */}
      {subscription?.active ? (
        <div className="bg-gradient-to-r from-[#0F172A] to-[#1e293b] text-white p-6 border border-[#E07A3A]/30" data-testid="subscription-active-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-[#25D366]" />
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#25D366]">Active</span>
              </div>
              <p className="font-['Playfair_Display'] text-2xl capitalize">{subscription.tier} · 90-Day Plan</p>
              <p className="text-white/50 text-sm mt-1">Expires {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : '-'}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-[#E07A3A]">
                <Clock className="h-4 w-4" />
                <span className="font-['Playfair_Display'] text-3xl font-bold">{subscription.days_remaining}</span>
              </div>
              <p className="text-[10px] text-white/40 tracking-wider uppercase">days remaining</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 p-5 flex items-start gap-3" data-testid="subscription-inactive-card">
          <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900 text-sm mb-1">No active subscription</p>
            <p className="text-xs text-amber-800">Choose a 90-day plan below to unlock unlimited listings, AI tools, and priority lead alerts.</p>
          </div>
        </div>
      )}

      {/* Country selector */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold">Billing region:</span>
        {['UK', 'IN', 'CA'].map(code => (
          <button
            key={code}
            onClick={() => setCountry(code)}
            className={`px-3 py-1 text-xs tracking-widest uppercase font-bold transition-colors ${country === code ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            data-testid={`billing-country-${code}`}
          >
            {code}
          </button>
        ))}
      </div>

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="plan-cards">
        {packages.map(pkg => {
          const tier = pkg.tier;
          const details = TIER_DETAILS[tier] || TIER_DETAILS.starter;
          const Icon = details.icon;
          const isPopular = details.popular;
          const isCurrent = subscription?.tier === tier && subscription?.active;
          return (
            <div
              key={pkg.package_id}
              className={`relative border p-6 transition-all hover:-translate-y-1 ${isPopular ? 'border-[#E07A3A] shadow-lg' : 'border-slate-200'} ${isCurrent ? 'ring-2 ring-[#25D366]' : ''}`}
              data-testid={`plan-card-${tier}`}
            >
              {isPopular && (
                <span className="absolute -top-3 left-6 bg-[#E07A3A] text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-1">Most Popular</span>
              )}
              {isCurrent && (
                <span className="absolute -top-3 right-6 bg-[#25D366] text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-1">Current Plan</span>
              )}
              <Icon className="h-6 w-6 mb-3" style={{ color: details.color }} />
              <h3 className="font-['Playfair_Display'] text-xl capitalize mb-1">{tier}</h3>
              <p className="text-xs text-slate-400 tracking-wider uppercase mb-4">90-Day Access</p>

              <div className="mb-5">
                <p className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">{formatAmount(pkg.amount, pkg.currency)}</p>
                <p className="text-xs text-slate-400 mt-1">one-off · no subscription trap</p>
              </div>

              <ul className="space-y-2 mb-6 text-sm">
                {details.features.map(f => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#E07A3A] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(pkg.package_id)}
                disabled={busyPackage === pkg.package_id || isCurrent}
                className={`w-full py-3 tracking-widest uppercase font-bold text-xs transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed ${isPopular ? 'bg-[#E07A3A] hover:bg-[#C4652E] text-white' : 'bg-[#0F172A] hover:bg-[#1e293b] text-white'}`}
                data-testid={`subscribe-btn-${tier}`}
              >
                {isCurrent ? 'Current Plan' : busyPackage === pkg.package_id ? (
                  <span className="flex items-center justify-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Opening Stripe…</span>
                ) : subscription?.active ? 'Extend / Upgrade' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-slate-400 text-center tracking-wider">
        Secure checkout powered by Stripe · Cards accepted: Visa · Mastercard · Amex · Apple Pay · Google Pay
      </p>
    </div>
  );
};

export default SubscriptionPlans;
